import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { i18nResponseDto } from '@tiendanube/common';
import { CouponValueTypes } from '@tiendanube/common/src/domains/store-activity-logs/enums';
import { dateFormat, Format } from 'commons/utils/date';
import {
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
  DEFAULT_TIMEZONE,
} from 'domains/Marketing/Coupons/TimeZoneConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

const parseCurrency = ({ value, currency }) =>
  `${currency}${parseFloat(value)}`;
const parsePercentage = ({ value }) => `${parseFloat(value)}%`;
const translateValue = ({ t, key, value }) =>
  t(`keysTranslations.${key}.type.${value}`).toLowerCase();
const parseValue = ({ eventData, value, currency }) => {
  if (eventData.new.type === CouponValueTypes.PERCENTAGE)
    return parsePercentage({ value });
  if (eventData.new.type === CouponValueTypes.ABSOLUTE)
    return parseCurrency({ value, currency });
  return value;
};

const parseDate = ({
  t,
  key,
  value,
  timezone: userTimezone = DEFAULT_TIMEZONE,
}: {
  t: (key: string, options?: any) => string;
  key: string;
  value: string | string[];
  timezone?: string;
}): string => {
  if (typeof value === 'string') {
    return dateFormat(value, Format.DD_MM_YY, userTimezone);
  }

  const [dateRaw, time] = value;

  if (!dateRaw) {
    const emptyValue = emptyValueFor(key);
    return t(`keysTranslations.${emptyValue}`);
  }

  const getDefaultTime = () => {
    if (key === 'end_date') return DEFAULT_END_TIME;

    return DEFAULT_START_TIME;
  };

  const timeToUse = time || getDefaultTime();
  const utcDateTime = `${dateRaw}T${timeToUse}:00.000Z`;

  const localDateTime = dayjs.utc(utcDateTime).tz(userTimezone);
  const dateFormatted = localDateTime.format('DD/MM/YY');
  const timeFormatted = time ? localDateTime.format('HH:mm') : '';

  if (!timeFormatted) {
    return dateFormatted;
  }

  return t(`keysTranslations.${key}.fromTo`, {
    date: dateFormatted,
    time: timeFormatted,
    interpolation: { escapeValue: false },
  });
};

const translateNames = ({ key, eventData, language }) => {
  const lang = language.split('-')[0];
  return eventData.new[key]
    .map(({ name }: { name: i18nResponseDto }) => name[lang])
    .join(', ');
};

export const createValueTransformationMap = (timezone = DEFAULT_TIMEZONE) => ({
  start_date: (params) =>
    parseDate({
      ...params,
      timezone,
    }),
  end_date: (params) =>
    parseDate({
      ...params,
      timezone,
    }),
  min_price: parseCurrency,
  value: parseValue,
  categories: translateNames,
  products: translateNames,
  limits: translateValue,
  quantity: translateValue,
  client: translateValue,
  active: translateValue,
  type: translateValue,
  valid: translateValue,
  first_consumer_purchase: translateValue,
  includes_shipping: translateValue,
  combines_with_other_discounts: translateValue,
  only_cheapest_shipping: translateValue,
  max_discount_amount: parseCurrency,
});

export const valueTransformationMap = createValueTransformationMap();

export const emptyValueFor = (key: string) => `${key}.empty`;
